/**
 * Города и часовые пояса для шага 1 «Площадка» (007, research R15, FR-017). Пояс ставится по городу и показывается
 * примером: «08:00 на площадке = 08:00 по Екатеринбургу». Своего города нет в списке — «Другой город» и выбор пояса.
 * Пояс хранится в `objects.tz_offset` в виде `+05:00`. Функции чистые.
 */

export interface City {
  readonly name: string;
  readonly tzOffset: string;
}

/** Города РФ с поясами: сначала Урал (+05:00), потом остальные по поясу. */
export const CITIES: readonly City[] = [
  { name: 'Екатеринбург', tzOffset: '+05:00' },
  { name: 'Нижний Тагил', tzOffset: '+05:00' },
  { name: 'Тюмень', tzOffset: '+05:00' },
  { name: 'Челябинск', tzOffset: '+05:00' },
  { name: 'Пермь', tzOffset: '+05:00' },
  { name: 'Курган', tzOffset: '+05:00' },
  { name: 'Уфа', tzOffset: '+05:00' },
  { name: 'Москва', tzOffset: '+03:00' },
  { name: 'Санкт-Петербург', tzOffset: '+03:00' },
  { name: 'Казань', tzOffset: '+03:00' },
  { name: 'Самара', tzOffset: '+04:00' },
  { name: 'Омск', tzOffset: '+06:00' },
  { name: 'Новосибирск', tzOffset: '+07:00' },
];

/** Пункт списка для своего города: тогда пояс выбирают из `RU_TZ_OFFSETS`. */
export const OTHER_CITY = 'Другой город';

/** Пояса России: от Калининграда (+02:00) до Камчатки (+12:00). */
export const RU_TZ_OFFSETS: readonly string[] = Array.from(
  { length: 11 },
  (_, i) => `+${String(i + 2).padStart(2, '0')}:00`,
);

/** Пояс города из списка; нет в списке — `null`. */
export function cityTz(name: string): string | null {
  const n = name.trim();
  return CITIES.find((c) => c.name === n)?.tzOffset ?? null;
}

/**
 * Пояс площадки для шага 1: город из списка — пояс по списку; другой город — указанный пояс России. Нет ни того, ни
 * другого — `null` (сервер отвечает `CITY_UNKNOWN`).
 */
export function resolveCityTz(city: string, tzOffset?: string): string | null {
  return cityTz(city) ?? (tzOffset && RU_TZ_OFFSETS.includes(tzOffset) ? tzOffset : null);
}

/** Город для сравнения времени в примере: по умолчанию Екатеринбург (сервер и первая площадка). */
export const TZ_REFERENCES = {
  ekb: { tzOffset: '+05:00', label: 'по Екатеринбургу' },
  msk: { tzOffset: '+03:00', label: 'по Москве' },
} as const;

function minutes(tz: string): number {
  const m = /^([+-])(\d{2}):(\d{2})$/.exec(tz);
  if (!m) throw new Error(`Пояс не в формате +ЧЧ:ММ: ${tz}`);
  const v = Number(m[2]) * 60 + Number(m[3]);
  return m[1] === '-' ? -v : v;
}

const hhmm = (min: number) =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

/** Пример для пояса: «08:00 на площадке = 06:00 по Москве»; другой день — «накануне» или «на следующий день». */
export function tzExample(tz: string, ref: keyof typeof TZ_REFERENCES = 'ekb'): string {
  const r = TZ_REFERENCES[ref];
  const t = 8 * 60 - minutes(tz) + minutes(r.tzOffset);
  const day = t < 0 ? ' накануне' : t >= 24 * 60 ? ' на следующий день' : '';
  return `08:00 на площадке = ${hhmm((t + 24 * 60) % (24 * 60))}${day} ${r.label}`;
}

/** Город и пояс, как в списке площадок: «Нижний Тагил · +05:00». */
export function cityLabel(city: string, tz: string): string {
  return `${city} · ${tz}`;
}
